import React, { useState } from 'react';
import { useRouter } from 'found';
import styled from 'styled-components';
import { commitMutation, fetchQuery, graphql } from 'react-relay';
import Button from '../../components/input/Button';
import TextInput from '../../components/input/TextInput';
import Spacer from '../../components/Spacer';
import {
  HeadingNoMargin,
  Paragraph,
  ParagraphLink,
} from '../../components/typography/Typography';
import { routes } from '../../routes';
import environment from '../../api/environment';
import Checkbox from '../../components/input/Checkbox';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import LogoGradientIcon from '../../components/LogoGradientIcon';
import { getCurrentAccount, setCurrentAccount } from '../../account_helpers';
import { attemptLogin } from './Login';
import { ScaleLoader } from 'react-spinners';
import { COUNTRY_OPTIONS } from '../../helpers';
import Dropdown from '../../components/input/Dropdown';

const Container = styled.div`
  width: 90vw;
  @media (min-width: 1200px) {
    width: 341px;
  }
`;

const Terms = styled.div`
  display: flex;
  align-items: center;
`;

const LogoButton = styled.a`
  padding: 10px;
  display: inline-flex;
  border-radius: 5px;
  transition: all 0.2s;
  &:hover {
    background: #f4f5ff;
  }
`;

const TermsText = styled(Paragraph)``;

const CountryInfoLink = styled.a`
  font-size: 12px;
  color: #9092aa;
  margin-left: 7px;
  cursor: pointer;
  &:hover {
    color: #414250;
  }
`;

const addUserMutation = graphql`
  mutation CustomerSignup_AddUserMutation(
    $firstName: String!
    $lastName: String!
    $email: String!
    $password: String!
    $country: String!
  ) {
    addUser(
      input: {
        firstname: $firstName
        lastname: $lastName
        email: $email
        password: $password
        country: $country
      }
    )
  }
`;

const attemptSignup = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  country: string
) => {
  const variables = {
    firstName,
    lastName,
    email,
    password,
    country,
  };

  await new Promise((resolve, reject) => {
    //@ts-ignore
    commitMutation(environment, {
      mutation: addUserMutation,
      variables,
      onCompleted: async (response: any, errors: unknown) => {
        if (errors) {
          // Display error
          // errorCallback(`${errors[0]?.extensions?.message}`);
          reject(errors);
          return;
        }

        resolve(response);
      },
      onError: (err: any) => {
        reject(err);
      },
    });
  });
};

function CustomerSignup() {
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    country: COUNTRY_OPTIONS[0].value,
    termsAgreed: false,
  });
  document.title = 'Sign Up - PayPI';
  const dispatch = useToastDispatch();
  const [loading, setLoading] = useState(false);
  const { router } = useRouter();

  const signup = async () => {
    if (!formState.termsAgreed) {
      dispatch({
        alert: 'warning',
        text: 'Please agree to the Terms and Conditions to continue',
        stayFor: 4000,
      });
      return;
    }

    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-325827681/cBUACKH6w4sDEOH4rpsB',
      event_callback: () => {},
    });

    setLoading(true);

    try {
      const { firstName, lastName, email, password, country } = formState;
      const resp = await attemptSignup(
        firstName,
        lastName,
        email,
        password,
        country
      );
      console.log('Success', resp);

      await attemptLogin(email, password);

      const data = await fetchQuery<any>(
        //@ts-ignore
        environment,
        graphql`
          query CustomerSignup_AccountQuery {
            viewer {
              userAccounts {
                id
              }
            }
          }
        `,
        {}
        //@ts-ignore
      ).toPromise();

      //

      if (data.viewer.userAccounts[0].id) {
        if (!getCurrentAccount()) {
          setCurrentAccount(data.viewer.userAccounts[0].id);
        }

        if (
          getCurrentAccount() &&
          !data.viewer.userAccounts.includes(getCurrentAccount())
        ) {
          setCurrentAccount(data.viewer.userAccounts[0].id);
        }
      } else {
        dispatch({
          alert: 'error',
          text: 'Unable to select account, please contact PayPI Support',
        });
        return;
      }

      router.push(routes.subscriptions);
    } catch (err) {
      setLoading(false);
      dispatch({
        alert: 'error',
        text: 'Unable to sign up, please try again',
      });
      console.warn('Err', err);
    }
  };

  const handleKey = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      signup();
    }
  };

  return (
    <Container>
      <LogoButton href={'https://paypi.dev'}>
        <LogoGradientIcon />
      </LogoButton>
      <Spacer vertical spacing={2} />
      <HeadingNoMargin>Sign up to PayPI</HeadingNoMargin>
      <Spacer vertical spacing={2} />
      <Paragraph>All your APIs, all in one place</Paragraph>
      <Spacer vertical spacing={4} />
      <TextInput
        label='First name'
        fullWidth
        validate='text'
        value={formState.firstName}
        onKeyDown={(e) => handleKey(e)}
        onChange={(text) => setFormState({ ...formState, firstName: text })}
      />
      <Spacer vertical spacing={2} />
      <TextInput
        label='Last name'
        fullWidth
        validate='text'
        value={formState.lastName}
        onKeyDown={(e) => handleKey(e)}
        onChange={(text) => setFormState({ ...formState, lastName: text })}
      />
      <Spacer vertical spacing={2} />

      <TextInput
        label='Email'
        fullWidth
        validate='email'
        value={formState.email}
        onKeyDown={(e) => handleKey(e)}
        onChange={(text) => setFormState({ ...formState, email: text })}
      />
      <Spacer vertical spacing={2} />
      <TextInput
        label='Password'
        fullWidth
        type={'password'}
        validate='password'
        value={formState.password}
        onKeyDown={(e) => handleKey(e)}
        onChange={(text) => setFormState({ ...formState, password: text })}
      />
      <Spacer vertical spacing={2} />
      <Dropdown
        label='Country'
        options={COUNTRY_OPTIONS}
        onChange={(option) => {
          setFormState({ ...formState, country: option.value });
        }}
        width={'100%'}
        defaultOption={COUNTRY_OPTIONS[0]}
        extraLabelInfo={
          <CountryInfoLink
            href='https://docs.paypi.dev/support/supported-countries'
            target='_blank'
          >
            Country not listed?
          </CountryInfoLink>
        }
      />
      <Spacer vertical spacing={2} />
      <Terms
        onClick={() =>
          setFormState({ ...formState, termsAgreed: !formState.termsAgreed })
        }
      >
        <Checkbox selected={formState.termsAgreed} />
        <Spacer horizontal spacing={2} />
        <TermsText>
          I am creating a PayPI account, and agree to PayPI's{' '}
          <ParagraphLink
            href='https://docs.paypi.dev/legal/terms-and-conditions'
            target='_blank'
          >
            Terms and Conditions
          </ParagraphLink>{' '}
          and{' '}
          <ParagraphLink
            href='https://docs.paypi.dev/legal/privacy-policy'
            target='_blank'
          >
            Privacy Policy
          </ParagraphLink>
        </TermsText>
      </Terms>

      <Spacer vertical spacing={3} />
      <Button onClick={() => signup()} color='white'>
        {!loading && 'Create Account'}
        {loading && <ScaleLoader color={'#e4e4e6'} height={11} />}
      </Button>
    </Container>
  );
}

export default CustomerSignup;
